import React, { useState, useEffect, useMemo } from "react";
import {
  FaFilter,
  FaSearch,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import Footer from "../../components/footer";
import GetOutstandingDocuments from "../../hooks/getOutstandingDocuments";
import { fetchSinglePatient } from "../../services/patient";
import moment from "moment";
import { formatUtcTimestamp, formatUtcDate } from "../../utils/helper";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import Switch from "react-switch";
import PickDate from "../../components/common/datePicker";
import usePreventBrowserBack from "../../hooks/usePreventBrowserBack";

const OutstandingList = (props) => {
   const author = useMemo(() => JSON.parse(localStorage.getItem("user")), []);
  usePreventBrowserBack(Boolean(props.location?.state?.preventBrowserBack));

  // Enhanced filter states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState([]);
  const [selectedMeetingTypes, setSelectedMeetingTypes] = useState([]);
  const [selectedAuthor, setSelectedAuthor] = useState(null);
  const [availableAuthors, setAvailableAuthors] = useState([]);

  // Switch states for filters
  const [checkedDocumentType, setCheckedDocumentType] = useState({
    check: false,
    attr: "documentType",
  });
  const [checkedMeetingType, setCheckedMeetingType] = useState({
    check: false,
    attr: "meetingType",
  });
  const [checkedAuthor, setCheckedAuthor] = useState({
    check: false,
    attr: "author",
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  const { data, isLoading } = GetOutstandingDocuments(currentPage, pageSize);

  // Filter constants
  const documentTypes = [
    { value: "CAREPLAN_COLLABORATIVE", label: "Careplan - Collaborative" },
    { value: "CAREPLAN_INDIVIDUAL", label: "Careplan - Individual" },
    { value: "FUNCTIONAL_SCORE", label: "Functional Scores" },
  ];

  const meetingTypes = [
    { value: "DISCHARGE_PLANNING", label: "Discharge Planning" },
    { value: "FAMILY_CASE_CONFERENCE", label: "Family Case Conference" },
    { value: "WEEKLY_CONFERENCE_ROUNDS", label: "Weekly Conference Rounds" },
  ];

  // Extract available authors from data
  useEffect(() => {
    if (data?.data?.content && data.data.content.length > 0) {
      const authorsMap = new Map();

      // For now, we'll use the current user as the only author since that's what's shown in the table
      // In a real implementation, you might want to fetch authors from the API or extract from data
      if (author) {
        const authorKey = `${author?.lastName}, ${author?.firstName}: ${
          author?.organizations[0]?.careProvider?.position?.abbreviation || "N/A"
        }`;
        authorsMap.set(author.id || "current", {
          id: author.id || "current",
          name: authorKey,
          firstName: author?.firstName,
          lastName: author?.lastName,
          position:
            author?.organizations[0]?.careProvider?.position?.abbreviation ||
            "N/A",
        });
      }

      setAvailableAuthors(Array.from(authorsMap.values()));
    }
  }, [data, author]);

  // Enhanced filter handlers
  const handleChecked = (status, e, id) => {
    switch (id) {
      case "documentType":
        setCheckedDocumentType({ check: status, attr: id });
        if (!status) {
          setSelectedDocumentTypes([]);
        }
        break;
      case "meetingType":
        setCheckedMeetingType({ check: status, attr: id });
        if (!status) {
          setSelectedMeetingTypes([]);
        }
        break;
      case "author":
        setCheckedAuthor({ check: status, attr: id });
        if (!status) {
          setSelectedAuthor(null);
        }
        break;
      default:
        break;
    }
  };

  // Date filter functions
  const handleTodayFilter = () => {
    const today = moment().startOf("day").toDate();
    const tomorrow = moment().add(1, "day").startOf("day").toDate();

    setStartDate(today);
    setEndDate(tomorrow);
  };

  const handleLast7DaysFilter = () => {
    const sevenDaysAgo = moment().subtract(7, "days").startOf("day").toDate();
    const today = moment().startOf("day").toDate();

    setStartDate(sevenDaysAgo);
    setEndDate(today);
  };

  const handleLast30DaysFilter = () => {
    const thirtyDaysAgo = moment().subtract(30, "days").startOf("day").toDate();
    const today = moment().startOf("day").toDate();

    setStartDate(thirtyDaysAgo);
    setEndDate(today);
  };

  // Format date for API (YYYY-MM-DDTHH:mm:ss.SSS)
  const formatDateForAPI = (date) => {
    if (!date || !moment(date).isValid()) return null;
    return moment(date).startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS");
  };

  const handleStartDateChange = (date) => {
    const normalized = new Date(date);
    const offset = normalized.getTimezoneOffset();
    normalized.setMinutes(normalized.getMinutes() - offset);
    setStartDate(normalized);
  };

  const handleEndDateChange = (date) => {
    const normalized = new Date(date);
    const offset = normalized.getTimezoneOffset();
    normalized.setMinutes(normalized.getMinutes() - offset);
    setEndDate(normalized);
  };

  // Document type selection handler
  const handleDocumentTypeSelection = (docType, isChecked) => {
    if (isChecked) {
      setSelectedDocumentTypes([...selectedDocumentTypes, docType]);
    } else {
      setSelectedDocumentTypes(
        selectedDocumentTypes.filter((type) => type !== docType)
      );
    }
  };

  // Meeting type selection handler
  const handleMeetingTypeSelection = (meetingType, isChecked) => {
    if (isChecked) {
      setSelectedMeetingTypes([...selectedMeetingTypes, meetingType]);
    } else {
      setSelectedMeetingTypes(
        selectedMeetingTypes.filter((type) => type !== meetingType)
      );
    }
  };

  // Author selection handler (radio button logic)
  const handleAuthorSelection = (authorId) => {
    if (selectedAuthor === authorId) {
      setSelectedAuthor(null);
    } else {
      setSelectedAuthor(authorId);
    }
  };

  // Pagination handlers
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil((data?.data?.totalElements || 0) / pageSize);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getItemTimestamp = (item) =>{
   return item?.createdAt || item?.dateSaved || item?.dateCreated || null;
  }

  const getDateSaved = (item) => {
    return item?.entryDate || item?.dateSaved || item?.dateCreated || null;
  };

  const formatTimestamp = formatUtcTimestamp;
  const formatDate = formatUtcDate;

  const formatLabel = (value) => {
    if (!value) return "";
    return String(value)
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/(^|\s)\S/g, (l) => l.toUpperCase());
  };

  const getMeetingTypeLabel = (value) => {
    if (!value) return "";
    const match = meetingTypes.find((item) => item.value === value);
    if (match) return match.label;
    if (typeof value === "string") {
      return formatLabel(value);
    }
    return String(value);
  };

  const getDocumentTypeLabel = (item) => {
    if (!item) return "Unknown";
    if (item.documentType === "FUNCTIONAL_SCORE") {
      return "Functional Scores Entry";
    }

    const carePlanType = item.carePlanType || item.type;
    const meetingType = item.typeOfMeeting || item.meetingType || item.meeting;
    if (
      item.documentType === "INDIVIDUAL_CAREPLAN" ||
      carePlanType === "INDIVIDUAL"
    ) {
      return "Individual Careplan";
    }
    if (
      item.documentType === "COLLABORATIVE_CAREPLAN" ||
      carePlanType === "COLLABORATIVE"
    ) {
      const meetingLabel = getMeetingTypeLabel(meetingType);
      return meetingLabel ? meetingLabel : "Collaborative Careplan";
    }
    if (item.documentType === "CAREPLAN") {
      if (carePlanType === "INDIVIDUAL") return "Individual Careplan";
      if (carePlanType === "COLLABORATIVE") {
        const meetingLabel = getMeetingTypeLabel(meetingType);
        return meetingLabel ? meetingLabel : "Collaborative Careplan";
      }
      return "Careplan";
    }
    return item.documentType ? formatLabel(item.documentType) : "Unknown";
  };

  // Check if any filters are active
  const hasActiveFilters = () => {
    return (
      (filterStartDate && filterEndDate) ||
      (checkedDocumentType.check && selectedDocumentTypes.length > 0) ||
      (checkedMeetingType.check && selectedMeetingTypes.length > 0) ||
      (checkedAuthor.check && selectedAuthor) ||
      searchTerm.trim().length > 0
    );
  };

  // Filter data based on search term and enhanced filters
  const getFilteredData = () => {
    // If no filters are active, return server-side paginated data directly
    if (!hasActiveFilters()) {
      return data?.data?.content || [];
    }

    // When filters are active, we need to filter the current page's data
    // Note: This only filters the current page. For full filtering across all pages,
    // you would need to fetch all data or implement server-side filtering
    let baseData = data?.data?.content || [];

    // Apply enhanced filters first
    if (filterStartDate && filterEndDate) {
      baseData = baseData.filter((item) => {
        const itemDate = moment(getDateSaved(item));
        const start = moment(filterStartDate);
        const end = moment(filterEndDate);
        return itemDate.isBetween(start, end, null, "[]");
      });
    }

    if (checkedDocumentType.check && selectedDocumentTypes.length > 0) {
      baseData = baseData.filter((item) => {
        return selectedDocumentTypes.includes(item.documentType);
      });
    }

    if (checkedMeetingType.check && selectedMeetingTypes.length > 0) {
      // Note: This filter would need to be implemented based on how meeting type is stored in the data
      // For now, we'll assume it's stored in a meetingType field
      baseData = baseData.filter((item) => {
        return selectedMeetingTypes.includes(
          item.typeOfMeeting || item.meetingType
        );
      });
    }

    if (checkedAuthor.check && selectedAuthor) {
      // Filter by selected author
      baseData = baseData.filter((item) => {
        // This would need to match the author field in the data
        // For now, we'll use a simple check against the current user
        return selectedAuthor === (author.id || "current");
      });
    }

    // Apply search term filter if present
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();

      baseData = baseData.filter((item) => {
        // Search in timestamp
        const dateSavedMatch = formatDate(getDateSaved(item))
          .toLowerCase()
          .includes(searchLower);
        const timestampMatch = formatTimestamp(getItemTimestamp(item))
          .toLowerCase()
          .includes(searchLower);

        // Search in patient name
        const patientFullName = `${item.firstName || ""} ${
          item.lastName || ""
        }`.toLowerCase();
        const patientLastFirst = `${item.lastName || ""}, ${
          item.firstName || ""
        }`.toLowerCase();
        const patientMatch =
          patientFullName.includes(searchLower) ||
          patientLastFirst.includes(searchLower);

        // Search in document type
        const documentTypeMatch = getDocumentTypeLabel(item)
          .toLowerCase()
          .includes(searchLower);

        // Search in author (using current user from localStorage)
        const authorFullName = `${author?.firstName || ""} ${
          author?.lastName || ""
        }`.toLowerCase();
        const authorLastFirst = `${author?.lastName || ""}, ${
          author?.firstName || ""
        }`.toLowerCase();
        const authorMatch =
          authorFullName.includes(searchLower) ||
          authorLastFirst.includes(searchLower);

        // Search in position
        const positionMatch =
          author?.organizations[0]?.careProvider?.position?.name
            ?.toLowerCase()
            .includes(searchLower);

        return (
          dateSavedMatch ||
          timestampMatch ||
          patientMatch ||
          documentTypeMatch ||
          authorMatch ||
          positionMatch
        );
      });
    }

    // When filters are active, return filtered results without additional pagination
    // since the API already paginated the data
    return baseData;
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  // Enhanced search function
  const handleEnhancedSearch = () => {
    const formattedStartDate = formatDateForAPI(startDate);
    const formattedEndDate = formatDateForAPI(endDate);

    setFilterStartDate(formattedStartDate);
    setFilterEndDate(formattedEndDate);

    // Close the modal
    window.$("#filterModal").modal("hide");
  };

  // Clear all filters
  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setSelectedDocumentTypes([]);
    setSelectedMeetingTypes([]);
    setSelectedAuthor(null);
    setCheckedDocumentType({ check: false, attr: "documentType" });
    setCheckedMeetingType({ check: false, attr: "meetingType" });
    setCheckedAuthor({ check: false, attr: "author" });
  };

  // Get total count of filtered results (before pagination)
  const getTotalFilteredCount = () => {
    // If no filters are active, return server-side total
    if (!hasActiveFilters()) {
      return data?.data?.totalElements || 0;
    }

    // When filters are active, count filtered items on current page
    // Note: This only counts filtered items on the current page, not across all pages
    let baseData = data?.data?.content || [];

    // Apply enhanced filters first
    if (filterStartDate && filterEndDate) {
      baseData = baseData.filter((item) => {
        const itemDate = moment(getDateSaved(item));
        const start = moment(filterStartDate);
        const end = moment(filterEndDate);
        return itemDate.isBetween(start, end, null, "[]");
      });
    }

    if (checkedDocumentType.check && selectedDocumentTypes.length > 0) {
      baseData = baseData.filter((item) => {
        return selectedDocumentTypes.includes(item.documentType);
      });
    }

    if (checkedMeetingType.check && selectedMeetingTypes.length > 0) {
      baseData = baseData.filter((item) =>
        selectedMeetingTypes.includes(item.typeOfMeeting || item.meetingType)
      );
    }

    if (checkedAuthor.check && selectedAuthor) {
      baseData = baseData.filter((item) => {
        return selectedAuthor === (author.id || "current");
      });
    }

    // Apply search term filter if present
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();

      baseData = baseData.filter((item) => {
        const dateSavedMatch = formatDate(getDateSaved(item))
          .toLowerCase()
          .includes(searchLower);
        const timestampMatch = formatTimestamp(getItemTimestamp(item))
          .toLowerCase()
          .includes(searchLower);
        const patientFullName = `${item.firstName || ""} ${
          item.lastName || ""
        }`.toLowerCase();
        const patientLastFirst = `${item.lastName || ""}, ${
          item.firstName || ""
        }`.toLowerCase();
        const patientMatch =
          patientFullName.includes(searchLower) ||
          patientLastFirst.includes(searchLower);
        const documentTypeMatch = getDocumentTypeLabel(item)
          .toLowerCase()
          .includes(searchLower);
        const authorFullName = `${author?.firstName || ""} ${
          author?.lastName || ""
        }`.toLowerCase();
        const authorLastFirst = `${author?.lastName || ""}, ${
          author?.firstName || ""
        }`.toLowerCase();
        const authorMatch =
          authorFullName.includes(searchLower) ||
          authorLastFirst.includes(searchLower);
        const positionMatch =
          author?.organizations[0]?.careProvider?.position?.name
            ?.toLowerCase()
            .includes(searchLower);

        return (
          dateSavedMatch ||
          timestampMatch ||
          patientMatch ||
          documentTypeMatch ||
          authorMatch ||
          positionMatch
        );
      });
    }

    // Return count of filtered items on current page
    // Note: For accurate filtering across all pages, server-side filtering would be needed
    return baseData.length;
  };

  const handleGoToReview = async (documentId, documentType, item, date) => {
    const patientId = item.patientId;

    // Switch patient context
    localStorage.setItem("patient", patientId);

    // Fetch full patient profile before navigating so the navbar is complete
    try {
      const result = await fetchSinglePatient(patientId);
      if (result?.data) {
        localStorage.setItem("localPatient", JSON.stringify(result.data));
      }
    } catch (e) {
      // Fall back to whatever the outstanding doc gave us
      localStorage.setItem("localPatient", JSON.stringify({
        firstName: item.firstName,
        lastName: item.lastName,
        pommeId: patientId,
      }));
    }

    if (documentType === "FUNCTIONAL_SCORE") {
      localStorage.setItem("documentId", documentId);
      props.history.push("/hcp/patient/function_review_entry");
    } else if (documentType === "CAREPLAN" || String(documentType).endsWith("CAREPLAN")) {
      localStorage.setItem("outstandingCarePlanId", documentId);
      localStorage.setItem("outstandingCarePlanDate", date);
      localStorage.setItem("fromOutstanding", "true");
      props.history.push("/hcp/patient/care_review_plan");
    }
  };

  // Calculate pagination info
  const totalElements = getTotalFilteredCount();
  const totalPages = Math.ceil(totalElements / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;
  const visiblePageNumbers = useMemo(() => {
    const pageWindowSize = 3;

    if (totalPages <= 0) return [];
    if (totalPages <= pageWindowSize) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let startPage = Math.max(1, currentPage - 1);
    let endPage = Math.min(totalPages, startPage + pageWindowSize - 1);

    if (endPage - startPage + 1 < pageWindowSize) {
      startPage = Math.max(1, endPage - pageWindowSize + 1);
    }

    return Array.from(
      { length: endPage - startPage + 1 },
      (_, i) => startPage + i
    );
  }, [currentPage, totalPages]);

  return (
    <>
      <div className="container-fluid pt-12 pl-21 pb-12 pr-24 h-100 noScroll">
        <div className="card border-0 h-100">
          <div className="card-body mb-3 d-flex flex-column h-100">
            <div className="flex-shrink-0">
              <h5 className="font-weight-lighter">HCP: Manage Documents</h5>
              <hr />

              <div className="d-flex justify-content-between align-items-center flex-wrap">
                <h5 className="font-weight-lighter mt-2">
                  Outstanding Document List
                  {(() => {
                    const isSearchActive = searchTerm.trim().length > 0;
                    const isFilterActive =
                      hasActiveFilters() && !isSearchActive;

                    if (hasActiveFilters()) {
                      return (
                        <>
                          <br />
                          <small className="text_primary">
                            {isSearchActive && isFilterActive
                              ? "Search & Filter"
                              : isSearchActive
                              ? "Search"
                              : "Filter"}{" "}
                            results: {getTotalFilteredCount()} of{" "}
                            {data?.data?.totalElements || 0} documents
                          </small>
                        </>
                      );
                    }
                    return null;
                  })()}
                </h5>
                <div
                  className="d-flex align-items-center"
                  style={{ width: 355 }}
                >
                  <div className="input-group bg_muted rounded">
                    <div className="input-group-prepend">
                      <span
                        className="input-group-text border-0 bg-transparent text-muted pr-3"
                        id="basic-addon1"
                      >
                        <FaSearch size={16} color="#c2c2c2" />
                      </span>
                    </div>
                    <input
                      type="text"
                      className="form-control border-none bg_muted f-14 text-black outline-none rounded mr-3"
                      placeholder="Search"
                      aria-label="Search"
                      aria-describedby="basic-addon1"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <button
                    className="btn btn-outline-primary w-50 ml-3 rounded py-1"
                    data-toggle="modal"
                    data-target="#filterModal"
                  >
                    <FaFilter size={16} />
                    &nbsp;&nbsp; <small>Filter</small>
                  </button>
                </div>
              </div>
            </div>

            {/* Flexible Table Area */}
            <div
              className="flex-grow-1 d-flex flex-column mt-3"
              style={{ minHeight: 0 }}
            >
              <div
                className="flex-grow-1"
                style={{
                  overflowY: "auto",
                }}
              >
                <table className="table table-striped table-bordered mb-0 text-center">
                  <thead
                    className="head_light text-white"
                    style={{ position: "sticky", top: 0, zIndex: 10 }}
                  >
                    <tr>
                      <th scope="col">Document Creation Date</th>
                      <th scope="col">Patient</th>
                      <th scope="col">Document Type</th>
                      <th scope="col">Author</th>
                      <th scope="col">Position</th>
                      <th scope="col">Go to Document</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filteredData = getFilteredData();
                      return !isLoading && filteredData.length > 0 ? (
                        filteredData.map((item, i) => (
                          <tr key={i}>
                            <th>
                              <small>
                                {formatTimestamp(getItemTimestamp(item))}
                              </small>
                            </th>
                         
                            <td>
                              <small>
                                {item.lastName}, {item.firstName}
                              </small>
                            </td>
                            <td>
                              <small>{getDocumentTypeLabel(item)}</small>
                            </td>
                            <td>
                              <small>
                                {author?.lastName}, {author?.firstName}
                              </small>
                            </td>
                            <td>
                              <small>
                                {
                                  author?.organizations[0]?.careProvider
                                    ?.position?.abbreviation
                                }
                              </small>
                            </td>
                            <td>
                              <Link
                                className="border_success rounded-pill px-4"
                                onClick={() =>
                                  handleGoToReview(
                                    item.id,
                                    item.documentType,
                                    item,
                                    getDateSaved(item)
                                  )
                                }
                              >
                                <i className="fa fa-chevron-right" />
                              </Link>
                            </td>
                          </tr>
                        ))
                      ) : !isLoading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <small className="text-muted">
                              {searchTerm.trim()
                                ? `No outstanding documents found matching "${searchTerm}"`
                                : "No outstanding documents found"}
                            </small>
                          </td>
                        </tr>
                      ) : null;
                    })()}
                    {isLoading && (
                      <SkeletonTheme
                        baseColor="#e9ecef"
                        highlightColor="#f8f9fa"
                      >
                        {[...Array(10)].map((_, index) => (
                          <tr key={`skeleton-${index}`}>
                            <th>
                              <small>
                                <Skeleton width={90} height={12} />
                              </small>
                            </th>
                            <td>
                              <small>
                                <Skeleton width={90} height={12} />
                              </small>
                            </td>
                            <td>
                              <small>
                                <Skeleton width={120} height={12} />
                              </small>
                            </td>
                            <td>
                              <small>
                                <Skeleton width={100} height={12} />
                              </small>
                            </td>
                            <td>
                              <small>
                                <Skeleton width={100} height={12} />
                              </small>
                            </td>
                            <td>
                              <small>
                                <Skeleton width={80} height={12} />
                              </small>
                            </td>
                            <td>
                              <Skeleton width={20} height={16} />
                            </td>
                          </tr>
                        ))}
                      </SkeletonTheme>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Fixed Pagination Controls - Bottom of card */}
              {!isLoading && data?.data && data.data.content.length > 0 && (
                <div className="flex-shrink-0 d-flex justify-content-between align-items-center mt-4">
                  {/* Page Size Dropdown - Bottom Left */}
                  <div className="d-flex align-items-center">
                    <span
                      className="text-dark mr-2"
                      style={{ fontSize: "12px", fontWeight: "500" }}
                    >
                      Show
                    </span>
                    <select
                      className="border rounded"
                      style={{
                        width: "50px",
                        height: "24px",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: "#495057",
                        backgroundColor: "#fff",
                        border: "1px solid #ced4da",
                        borderRadius: "6px",
                        padding: "2px 4px",
                        outline: "none",
                      }}
                      value={pageSize}
                      onChange={(e) =>
                        handlePageSizeChange(parseInt(e.target.value))
                      }
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                      <option value={25}>25</option>
                    </select>
                    <span
                      className="text-dark ml-2"
                      style={{ fontSize: "12px", fontWeight: "500" }}
                    >
                      entries
                    </span>
                  </div>

                  {/* Pagination Navigation - Bottom Right */}
                  <div className="d-flex align-items-center">
                    <span
                      className="text-muted mr-4"
                      style={{ fontSize: "12px" }}
                    >
                      {totalElements > 0
                        ? `Showing ${Math.min(
                            (currentPage - 1) * pageSize + 1,
                            totalElements
                          )} to ${Math.min(
                            currentPage * pageSize,
                            totalElements
                          )} of ${totalElements} entries`
                        : "No entries to show"}
                    </span>

                    {totalPages > 1 && (
                      <nav aria-label="Page navigation">
                        <div className="d-flex align-items-center">
                          {/* Previous Button */}
                          <button
                            className="btn d-flex align-items-center justify-content-center mr-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              backgroundColor: !hasPreviousPage
                                ? "#d7d7d7"
                                : "#fff",
                              color: !hasPreviousPage
                                ? "rgba(83, 89, 107, 0.5)"
                                : "#495057",
                              cursor: !hasPreviousPage
                                ? "not-allowed"
                                : "pointer",
                              paddingLeft: "7px",
                              paddingRight: "7px",
                            }}
                            onClick={handlePreviousPage}
                            disabled={!hasPreviousPage}
                          >
                            <FaChevronLeft size={12} />
                          </button>

                          {/* Page Numbers */}
                          <div className="d-flex">
                            {visiblePageNumbers.map((pageNum) => (
                              <button
                                key={pageNum}
                                className="btn d-flex align-items-center justify-content-center mx-1"
                                style={{
                                  width: "24px",
                                  height: "24px",
                                  border:
                                    currentPage === pageNum
                                      ? "1px solid #53596b"
                                      : "1px solid #ced4da",
                                  borderRadius: "6px",
                                  backgroundColor:
                                    currentPage === pageNum
                                      ? "#53596b"
                                      : "#fff",
                                  color:
                                    currentPage === pageNum
                                      ? "#fff"
                                      : "#495057",
                                  fontSize: "12px",
                                  fontWeight: "500",
                                  cursor: "pointer",
                                }}
                                onClick={() => handlePageChange(pageNum)}
                              >
                                {pageNum}
                              </button>
                            ))}
                          </div>

                          {/* Next Button */}
                          <button
                            className="btn d-flex align-items-center justify-content-center ml-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              backgroundColor: !hasNextPage
                                ? "#d7d7d7"
                                : "#fff",
                              color: !hasNextPage
                                ? "rgba(83, 89, 107, 0.5)"
                                : "#495057",
                              cursor: !hasNextPage ? "not-allowed" : "pointer",
                              paddingLeft: "7px",
                              paddingRight: "7px",
                            }}
                            onClick={handleNextPage}
                            disabled={!hasNextPage}
                          >
                            <FaChevronRight size={12} />
                          </button>
                        </div>
                      </nav>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filter Modal */}
      <div
        className="modal fade"
        id="filterModal"
        tabIndex="-1"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content pt-3">
            <div className="modal-header border-0">
              <div className="flex-grow-1 text-center">
                <h5 className="text_primary" style={{ fontWeight: 600 }}>
                  Search Filter
                </h5>
              </div>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span
                  className="text_primary"
                  style={{ fontSize: 40, fontWeight: "600" }}
                  aria-hidden="true"
                >
                  &times;
                </span>
              </button>
            </div>
            <div className="modal-body mb-3 p-0">
              <div className="d-flex justify-content-between align-items-center mb-4 px-3">
                <button
                  className="btn bg_dark darkHover"
                  onClick={handleTodayFilter}
                >
                  <small>Today</small>
                </button>
                <button
                  className="btn bg_dark darkHover"
                  onClick={handleLast7DaysFilter}
                >
                  <small>Last 7 days</small>
                </button>
                <button
                  className="btn bg_dark darkHover"
                  onClick={handleLast30DaysFilter}
                >
                  <small>Last 30 days</small>
                </button>
              </div>
              <small className="text-muted ml-3">Custom Date</small>
              <hr className="my-2 mx-3" />
              <div className="row mb-3 mx-1">
                <div className="col-md-6">
                  <small className="text-muted">Start Date</small>
                  <PickDate
                    date={startDate}
                    setDate={handleStartDateChange}
                    required={false}
                  />
                </div>
                <div className="col-md-6">
                  <small className="text-muted">End Date</small>
                  <PickDate
                    date={endDate}
                    setDate={handleEndDateChange}
                    required={false}
                  />
                </div>
              </div>

              <Accordion allowZeroExpanded allowMultipleExpanded>
                <div className="chevron" />

                {/* Document Type Filter */}
                <AccordionItem uuid="document-type-filter">
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Document Type</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={
                        checkedDocumentType.attr === "documentType" &&
                        checkedDocumentType.check
                      }
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="documentType"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 py-2 px-4">
                    <small className="text-muted f-13">
                      Choose as many as applicable:
                    </small>
                    <div className="mt-2">
                      {documentTypes.map((docType, i) => (
                        <div
                          className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-2"
                          key={i}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-2"
                            id={`docTypeCheck${docType.value}`}
                            checked={selectedDocumentTypes.includes(
                              docType.value
                            )}
                            onChange={(e) => {
                              handleDocumentTypeSelection(
                                docType.value,
                                e.target.checked
                              );
                            }}
                            disabled={!checkedDocumentType.check}
                          />
                          <label
                            className="form-check-label f-13 ml-2"
                            htmlFor={`docTypeCheck${docType.value}`}
                          >
                            <small>{docType.label}</small>
                          </label>
                        </div>
                      ))}
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>

                {/* Meeting Type Filter */}
                <AccordionItem uuid="meeting-type-filter">
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Type of Meeting</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={
                        checkedMeetingType.attr === "meetingType" &&
                        checkedMeetingType.check
                      }
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="meetingType"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 py-2 px-4">
                    <small className="text-muted f-13">
                      Choose as many as applicable:
                    </small>
                    <div className="mt-2">
                      {meetingTypes.map((meetingType, i) => (
                        <div
                          className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-2"
                          key={i}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-2"
                            id={`meetingTypeCheck${meetingType.value}`}
                            checked={selectedMeetingTypes.includes(
                              meetingType.value
                            )}
                            onChange={(e) => {
                              handleMeetingTypeSelection(
                                meetingType.value,
                                e.target.checked
                              );
                            }}
                            disabled={!checkedMeetingType.check}
                          />
                          <label
                            className="form-check-label f-13 ml-2"
                            htmlFor={`meetingTypeCheck${meetingType.value}`}
                          >
                            <small>{meetingType.label}</small>
                          </label>
                        </div>
                      ))}
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>

                {/* Author Filter */}
                <AccordionItem uuid="author-filter">
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Author</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={
                        checkedAuthor.attr === "author" && checkedAuthor.check
                      }
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="author"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 py-2 px-4">
                    <small className="text-muted f-13">
                      Choose one author only:
                    </small>
                    <div className="mt-2">
                      {availableAuthors.map((author, i) => (
                        <div
                          className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-2"
                          key={i}
                        >
                          <input
                            type="radio"
                            className="form-check-input mt-2"
                            id={`authorRadio${author.id}`}
                            name="authorRadio"
                            checked={selectedAuthor === author.id}
                            onChange={() => handleAuthorSelection(author.id)}
                            disabled={!checkedAuthor.check}
                          />
                          <label
                            className="form-check-label f-13 ml-2"
                            htmlFor={`authorRadio${author.id}`}
                          >
                            <small>{author.name}</small>
                          </label>
                        </div>
                      ))}
                      {availableAuthors?.length === 0 && (
                        <div className="ml-4">
                          <small className="text-muted">
                            No authors found in current data
                          </small>
                        </div>
                      )}
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>
              </Accordion>
            </div>
            <div className="modal-footer d-flex justify-content-between align-items-center border-0">
              <button
                type="button"
                className="btn btn_outline_danger px-5"
                onClick={handleClearFilters}
                data-dismiss="modal"
              >
                CLEAR
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-5"
                onClick={handleEnhancedSearch}
              >
                SEARCH
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OutstandingList;
